<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :to="tag === 'router-link' ? to : undefined"
    :href="tag === 'a' ? href : undefined"
    :disabled="tag === 'button' ? (disabled || loading) : undefined"
    :aria-disabled="tag !== 'button' && (disabled || loading) ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :class="buttonClasses"
    @click="handleClick"
  >
    <LoadingSpinner v-if="loading" :size="spinnerSize" :color="spinnerColor" class="shrink-0" />
    <component v-else-if="iconLeft" :is="iconLeft" :class="iconClasses" aria-hidden="true" />

    <span v-if="loading && loadingText">{{ loadingText }}</span>
    <span v-else-if="$slots.default" class="inline-flex min-w-0 items-center"><slot /></span>

    <component v-if="iconRight && !loading" :is="iconRight" :class="iconClasses" aria-hidden="true" />
  </component>
</template>

<script setup>
// Botón del sistema (identidad DPB). Reglas: un solo `primary` por modal o
// barra de acciones; `danger` solo para la acción destructiva (`danger-outline`
// cuando no es la acción principal, p. ej. Rechazar junto a Aprobar); `secondary` /
// `outline` para Cancelar y acciones neutras; `ghost` para acciones terciarias.
// md mide 40 px (h-10), igual que los campos de common/fields.
import { computed } from "vue";
import LoadingSpinner from "./LoadingSpinner.vue";

const props = defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (value) =>
      ["primary", "secondary", "success", "danger", "danger-outline", "ghost", "outline", "link"].includes(value),
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["xs", "sm", "md", "lg", "xl"].includes(value),
  },
  type: {
    type: String,
    default: "button",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: "",
  },
  tag: {
    type: String,
    default: "button",
    validator: (value) => ["button", "a", "router-link"].includes(value),
  },
  to: {
    type: [String, Object],
    default: null,
  },
  href: {
    type: String,
    default: null,
  },
  iconLeft: {
    type: [Object, Function],
    default: null,
  },
  iconRight: {
    type: [Object, Function],
    default: null,
  },
  block: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: String,
    default: "md",
    validator: (value) => ["none", "sm", "md", "lg", "full"].includes(value),
  },
});

const emit = defineEmits(["click"]);

const SIZE_CLASSES = {
  xs: "h-7 px-2.5 gap-1.5 text-xs",
  sm: "h-8 px-3 gap-1.5 text-sm",
  md: "h-10 px-4 gap-2 text-sm",
  lg: "h-12 px-5 gap-2 text-base",
  xl: "h-12 px-6 gap-2 text-base",
};

// El enlace no lleva relleno ni alto fijo: se alinea con el texto que lo rodea.
const LINK_SIZE_CLASSES = {
  xs: "gap-1 text-xs",
  sm: "gap-1.5 text-sm",
  md: "gap-2 text-sm",
  lg: "gap-2 text-base",
  xl: "gap-2 text-base",
};

// Texto blanco solo sobre fondos con contraste AA (blue/red/green/amber 700 o
// el 600 de marca).
const VARIANT_CLASSES = {
  primary: "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800",
  secondary: "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-400",
  outline: "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-400",
  danger: "bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-800",
  "danger-outline": "border border-red-300 bg-white text-red-700 hover:border-red-400 hover:bg-red-50",
  success: "bg-success-700 text-white hover:bg-success-800",
  ghost: "text-gray-700 hover:bg-gray-100 hover:text-gray-900",
  link: "text-primary-600 hover:text-primary-800 underline-offset-4 hover:underline",
};

const ROUNDED_CLASSES = {
  none: "rounded-none",
  sm: "rounded",
  md: "rounded-lg",
  lg: "rounded-xl",
  full: "rounded-full",
};

const buttonClasses = computed(() => [
  "inline-flex items-center justify-center whitespace-nowrap font-semibold transition-colors duration-150",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
  "disabled:cursor-not-allowed disabled:opacity-50",
  props.tag !== "button" && (props.disabled || props.loading) ? "pointer-events-none opacity-50" : "",
  props.variant === "link" ? LINK_SIZE_CLASSES[props.size] : SIZE_CLASSES[props.size],
  VARIANT_CLASSES[props.variant],
  ROUNDED_CLASSES[props.rounded],
  props.block ? "w-full" : "",
  props.loading ? "cursor-wait" : "",
]);

const iconClasses = computed(() => (["xs", "sm"].includes(props.size) ? "h-4 w-4 shrink-0" : "h-5 w-5 shrink-0"));

const spinnerSize = computed(() => (["xs", "sm", "md"].includes(props.size) ? "xs" : "sm"));

const spinnerColor = computed(() =>
  ["primary", "danger", "success"].includes(props.variant) ? "white" : "gray"
);

const handleClick = (event) => {
  if (props.disabled || props.loading) {
    event.preventDefault();
    return;
  }
  emit("click", event);
};
</script>
